const isSecure = typeof window !== 'undefined' && (window.location.protocol === 'https:' || window.location.protocol === 'file:');
const wsProtocol = isSecure ? 'wss:' : 'ws:';
const wsHost = typeof window !== 'undefined' ? (window.location.hostname || 'localhost') : 'localhost';
const WS_URL = wsProtocol + '//' + wsHost + '/ws';

class NotificationService {
  constructor() {
    this.ws = null;
    this.listeners = [];
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
  }

  async init() {
    await this.requestPermission();
    this.connectWebSocket();
    this.registerServiceWorker();
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      console.log('[NotificationService] Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      console.log('[NotificationService] Permission already granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      console.log('[NotificationService] Permission:', permission);
      return permission === 'granted';
    }

    console.log('[NotificationService] Permission denied');
    return false;
  }

  registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('[NotificationService] SW registered:', registration.scope);
        })
        .catch((err) => {
          console.log('[NotificationService] SW registration failed:', err);
        });
    }
  }

  connectWebSocket() {
    console.log('[NotificationService] Connecting to WebSocket:', WS_URL);
    
    const socket = new WebSocket(WS_URL);
    this.ws = socket;

    socket.onopen = () => {
      console.log('[NotificationService] WS Connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          type: 'device_register',
          data: { 
            deviceId: this.getDeviceId(),
            platform: 'web',
            timestamp: Date.now()
          }
        }));
      }
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[NotificationService] WS message:', data);
        
        let notification = null;
        
        if (data.type === 'notification_created') {
          notification = data.data;
        } else if (data.type === 'push_notification') {
          notification = data.data;
        } else if (data.type === 'notification') {
          notification = data;
        }
        
        if (notification) {
          this.showNotification(notification);
          this.notifyListeners(notification);
        }
      } catch (e) {
        console.log('[NotificationService] Parse error:', e);
      }
    };

    socket.onclose = () => {
      console.log('[NotificationService] WS Disconnected');
      if (this.ws !== socket) return;
      this.ws = null;
      this.isConnected = false;
      this.reconnect();
    };

    socket.onerror = (err) => {
      console.log('[NotificationService] WS Error:', err);
    };
  }

  reconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`[NotificationService] Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      setTimeout(() => this.connectWebSocket(), 3000);
    }
  }

  showNotification(data) {
    const title = data.title || data.body || 'Kamelot Security';
    const body = data.body || data.message || '';
    const icon = data.icon || data.image || '/logo192.png';

    if (Notification.permission === 'granted') {
      new Notification(title, {
        body: body,
        icon: icon,
        badge: '/logo192.png',
        vibrate: [100, 50, 100],
        tag: 'kss-notification',
        data: { url: data.click_action || '/' }
      });
    }

    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title,
        body,
        icon
      });
    }
  }

  getDeviceId() {
    let deviceId = localStorage.getItem('kss_device_id');
    if (!deviceId) {
      deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('kss_device_id', deviceId);
    }
    return deviceId;
  }

  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    if (!callback) return;
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  notifyListeners(notification) {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (e) {
        console.error('[NotificationService] Listener error:', e);
      }
    });
  }

  async sendTestNotification() {
    const hasPermission = await this.requestPermission();
    if (hasPermission) {
      new Notification('Kamelot Security - Test', {
        body: 'Notifications are working!',
        icon: '/logo192.png'
      });
      return true;
    }
    return false;
  }
}

export const notificationService = new NotificationService();
export default notificationService;